import React, {useEffect, useState} from 'react';
import {BackButton, Button, Container, Input} from '../../components/@FreeModel';
import {Autocomplete, TextField} from '@mui/material';
import {useNavigate, useParams} from 'react-router-dom';
import Header from '../../components/general/Header/Header';
import {useDispatch, useSelector} from "react-redux";
import {selectApplication} from "../../db/selectors";
import {gql, queryAddressAutoComplete, queryApplication, updateApplication} from "../../db/model";
import {AddressOption, DeDuplicateAddressOptions} from "../../db/addressoptions";
import {useDebounce} from "../../utlis/debounce";
import {formatPhoneNumber} from "../../utlis/format";
import {Mixpanel} from "../../observability/Mixpanel";


const BusinessInfoDetails = (): React.ReactElement => {
  const navigate = useNavigate();
  const dispatch = useDispatch()
  const {applicationID} = useParams()
  const application = useSelector(selectApplication)

  const [street1, setStreet1] = useState(application.address?.street1 ?? '')
  const [street2, setStreet2] = useState(application.address?.street2 ?? '')
  const [city, setCity] = useState(application.address?.city ?? '')
  const [state, setState] = useState(application.address?.state ?? '')
  const [postalCode, setPostalCode] = useState(application.address?.postalCode ?? '')
  const [businessPhoneNumber, setBusinessPhoneNumber] = useState(application.businessPhoneNumber ?? '')
  const [website, setWebsite] = useState(application.website ?? '')
  const defaultAddressOption: AddressOption[] = []
  const [addressOptions, setAddressOptions] = useState(defaultAddressOption)

  const isValid = street1 !== "" || city !== "" || state !== "" || postalCode !== ""

  useEffect(() => {
    // @ts-ignore
    dispatch(gql({
      query: queryApplication,
      variables: {id: applicationID},
      onSuccess: (data) => {
        setStreet1(data.application?.address?.street1 ?? '')
        setStreet2(data.application?.address?.street2 ?? '')
        setCity(data.application?.address?.city ?? '')
        setState(data.application?.address?.state ?? '')
        setPostalCode(data.application?.address?.postalCode ?? '')
        setBusinessPhoneNumber(data.application?.businessPhoneNumber ?? '')
        setWebsite(data.application?.website ?? '')
      }
    }))
  }, [dispatch, applicationID, setStreet1, setStreet2, setCity, setState, setPostalCode, setBusinessPhoneNumber, setWebsite]);


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // @ts-ignore
    dispatch(gql({
      query: updateApplication,
      variables: {
        id: applicationID,
        form: {
          address: {
            street1,
            street2,
            city,
            state,
            postalCode
          },
          businessPhoneNumber,
          website
        }
      },
      onSuccess: (_) => {
        Mixpanel.track("contractor-business-info-added", {
          "invitation": applicationID,
        })
        navigate(`/contractors/${applicationID}/business-info/3`)
      }
    }))
  };

  const debouncedAutoComplete = useDebounce(() => {
    // @ts-ignore
    dispatch(gql({
      query: queryAddressAutoComplete,
      variables: {query: street1},
      usePublicHost: true,
      onSuccess: (appState) => {
        if (appState?.addressAutocomplete) {
          setAddressOptions(DeDuplicateAddressOptions({addressOptions: appState.addressAutocomplete}))
        }
      }
    }))
  }, 250)

  const onChange = (addressOption:  AddressOption | null) => {
    if (addressOption) {
      let address = addressOption.value
      setStreet1(address.street1 ?? street1)
      setCity(address.city ?? city)
      setState(address.state ?? state)
      setPostalCode(address.postalCode ?? postalCode)
    }
  }

  return (
    <Container>
      <Header title="Add Business Info"/>
      <BackButton to={`/contractors/${applicationID}/business-info/1`}/>
      <br/>

      <form onSubmit={handleSubmit}>
        <Autocomplete
          // documented manner of debounced autocomplete by mui: https://mui.com/material-ui/react-autocomplete/#search-as-you-type
          freeSolo
          id="address-1"
          options={addressOptions}
          inputValue={street1}
          onInputChange={(event, newValue) => {
            setStreet1(newValue);
            debouncedAutoComplete()
          }}
          value={street1}
          onChange={(event, newValue) => onChange(newValue as AddressOption)}
          renderInput={(params) =>
              <TextField
                  {...params}
                  label="Address line 1"
                  variant="standard"
              />
          }
        ></Autocomplete>


        <Input
          label="Address line 2"
          id="address-2"
          type="text"
          value={street2}
          onChange={e => setStreet2(e.target.value)}
        />

        <Input
          label="City"
          id="city"
          type="text"
          value={city}
          onChange={e => setCity(e.target.value)}
          required
        />

        <Input
          label="State/Region"
          id="state-region"
          type="text"
          value={state}
          onChange={e => setState(e.target.value)}
          required
        />

        <Input
          label="Postal code"
          id="postal-code"
          type="text"
          value={postalCode}
          onChange={e => setPostalCode(e.target.value)}
          required
        />

        <Input
          label="Business phone number (optional)"
          id="business-phone"
          type="tel"
          value={formatPhoneNumber(businessPhoneNumber)}
          onChange={e => setBusinessPhoneNumber(e.target.value)}
        />

        <Input
          label="Website URL (optional)"
          id="web-url"
          type="url"
          value={website}
          onChange={e => setWebsite(e.target.value)}
        />

        <br/>
        <br/>
        <br/>

        <Button disabled={!isValid}>Continue</Button>
      </form>
    </Container>
  );
};

export default BusinessInfoDetails;
